/**
 * CommonMultiSelect Component Module
 * 
 * Provides a searchable dropdown multi-select component with advanced selection features
 * Allows users to select multiple items from a list with search filtering and bulk actions
 * 
 * Features:
 * - Searchable options with case-insensitive filtering
 * - Select/deselect individual items
 * - Select All/Deselect All filtered items
 * - Search-aware bulk selection (only affects filtered results)
 * - Customizable display field name
 * - Apply/Cancel/Clear actions with confirmation
 * - Outside click detection for dropdown closure
 * - Responsive design with scrollable list and sticky footer
 * - Checkbox indicators for selection state
 * 
 * Component Structure:
 * - Input field with search icon and chevron
 * - Scrollable dropdown list with items and Select All option
 * - Sticky footer with Apply, Cancel, and Clear buttons
 * - Dynamic selected count display
 * 
 * State Management:
 * - localSelected: Local selection state (independent until Apply)
 * - searchTerm: Current search query text
 * - isOpen: Dropdown visibility state
 * 
 * Dependencies:
 * - React hooks: useState, useEffect, useRef
 * - lucide-react: Search and ChevronDown icons
 * - Bootstrap CSS classes for styling
 * 
 * @example
 * // Basic multi-select with objects
 * const departments = [
 *   { id: 1, name: 'Engineering', fullName: 'Engineering Department' },
 *   { id: 2, name: 'Sales', fullName: 'Sales Department' },
 *   { id: 3, name: 'Marketing', fullName: 'Marketing Department' }
 * ];
 * 
 * const [selected, setSelected] = useState([]);
 * return (
 *   <CommonMultiSelect
 *     options={departments}
 *     selectedValues={selected}
 *     onSelect={setSelected}
 *     displayValue="name"
 *     placeholder="Select departments..."
 *   />
 * );
 * 
 * @example
 * // With onRemove callback
 * <CommonMultiSelect
 *   options={items}
 *   selectedValues={selected}
 *   onSelect={handleSelect}
 *   onRemove={handleRemove}
 *   displayValue="label"
 * />
 */

import React, { useState, useEffect, useRef } from "react";
import { Search, ChevronDown } from "lucide-react";

/**
 * CommonMultiSelect Component
 * Advanced multi-select dropdown with search and bulk selection features
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array<Object>} [props.options=[]] - Array of selectable items
 *                                            Each item must have an 'id' property
 * @param {Array<Object>} [props.selectedValues=[]] - Currently selected items (controlled)
 * @param {Function} [props.onSelect] - Callback when Apply is clicked
 *                                     Receives array of selected items
 * @param {Function} [props.onRemove] - Callback when Clear is clicked
 *                                     Receives empty array
 * @param {string} [props.displayValue="name"] - Property name to display in list
 * @param {string} [props.placeholder="Select options"] - Placeholder text when empty
 * 
 * @returns {JSX.Element} Multi-select dropdown component
 */
export const CommonMultiSelect = ({
  options = [],
  selectedValues = [],
  onSelect,
  onRemove,
  displayValue = "name",
  placeholder = "Select options",
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  // Local selection state - changes only apply when "Apply" button clicked
  const [localSelected, setLocalSelected] = useState(selectedValues || []);
  
  // Search/filter input text
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dropdown visibility state
  const [isOpen, setIsOpen] = useState(false);
  
  // Reference to dropdown container for outside-click detection
  const dropdownRef = useRef(null);

  // ============================================================================
  // EFFECTS - DROPDOWN MANAGEMENT
  // ============================================================================
  
  /**
   * Close dropdown when clicking outside the component
   * Prevents dropdown staying open while clicking elsewhere
   * 
   * Process:
   * 1. Listens for mousedown events on document
   * 2. Checks if click is outside dropdownRef
   * 3. Closes dropdown if outside click detected
   * 4. Removes listener on component unmount
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /**
   * Sync local selection with parent-controlled selectedValues
   * Called when parent component updates selectedValues prop
   * Important: Only called when Apply/Cancel changes selectedValues
   */
  useEffect(() => {
    setLocalSelected(selectedValues);
  }, [selectedValues]);

  // ============================================================================
  // FILTERING AND SELECTION LOGIC
  // ============================================================================
  
  /**
   * Filter options based on search term
   * Case-insensitive comparison on displayValue property
   */
  const filteredItems = options.filter((item) =>
    item[displayValue]?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /**
   * Check if all currently filtered items are selected
   * Used for "Select All" checkbox state
   * 
   * Logic:
   * - True only if: filtered items exist AND all are in localSelected
   * - False if: no filtered items OR any filtered item is not selected
   */
  const allFilteredSelected =
    filteredItems.length > 0 &&
    filteredItems.every((fi) => localSelected.some((si) => si.id === fi.id));

  // ============================================================================
  // EVENT HANDLERS - SELECTION
  // ============================================================================
  
  /**
   * Toggle individual item selection state
   * Adds item to selection if not selected, removes if selected
   * 
   * @param {Object} item - Item to toggle (must have id property)
   */
  const toggleItem = (item) => {
    if (localSelected.some((i) => i.id === item.id)) {
      // Remove item from selection
      setLocalSelected((prev) => prev.filter((i) => i.id !== item.id));
    } else {
      // Add item to selection
      setLocalSelected((prev) => [...prev, item]);
    }
  };

  /**
   * Handle "Select All" checkbox for filtered items
   * 
   * Logic:
   * - If all filtered items already selected: Remove them from selection
   * - If not all selected: Add filtered items to current selection (no duplicates)
   * 
   * Note: Only affects filtered items, preserves non-filtered selections
   */
  const handleSelectAll = () => {
    if (allFilteredSelected) {
      // Remove only the filtered items from selection
      setLocalSelected((prev) =>
        prev.filter((si) => !filteredItems.some((fi) => fi.id === si.id))
      );
    } else {
      // Add filtered items to current selection (avoid duplicates)
      setLocalSelected((prev) => {
        const ids = new Set(prev.map((p) => p.id));
        const union = [...prev];
        filteredItems.forEach((fi) => {
          if (!ids.has(fi.id)) union.push(fi);
        });
        return union;
      });
    }
  };

  // ============================================================================
  // EVENT HANDLERS - DROPDOWN ACTIONS
  // ============================================================================
  
  /**
   * Apply button click handler
   * Commits localSelected changes to parent and closes dropdown
   * 
   * Process:
   * 1. Calls onSelect callback with current localSelected
   * 2. Closes dropdown
   */
  const handleApply = () => {
    if (onSelect) onSelect(localSelected);
    setIsOpen(false);
  };

  /**
   * Cancel button click handler
   * Discards localSelected changes and reverts to previous state
   * 
   * Process:
   * 1. Resets localSelected to parent's selectedValues
   * 2. Closes dropdown
   * 3. Clears search term
   */
  const handleCancel = () => {
    setLocalSelected(selectedValues);
    setIsOpen(false);
  };

  /**
   * Clear button click handler
   * Removes all selections and notifies parent
   * 
   * Process:
   * 1. Clears all selections (localSelected = [])
   * 2. Calls onRemove callback with empty array
   */
  const handleClear = () => {
    setLocalSelected([]);
    if (onRemove) onRemove([]);
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  
  /**
   * Generate display text for selected items in input field
   * Shows placeholder when empty, count when closed
   * 
   * Returns:
   * - Empty string: No items selected
   * - "X selected": 1 item selected, shows item name
   * - "N items selected": 2+ items selected, shows count
   */
  const getSelectedText = () => {
    if (localSelected.length === 0) return "";
    if (localSelected.length === 1)
      return `${localSelected[0][displayValue]} selected`;
    return `${localSelected.length} items selected`;
  };

  // ============================================================================
  // COMPONENT RENDER
  // ============================================================================
  
  return (
    <div className="position-relative mb-3" ref={dropdownRef}>
      {/* ================================================================== */}
      {/* INPUT FIELD WITH SEARCH ICON AND DROPDOWN CHEVRON */}
      {/* ================================================================== */}
      <div
        className="form-control d-flex align-items-center justify-content-between w-100"
        style={{ cursor: "pointer", height: "27px" }}
        onClick={() => setIsOpen(true)}
      >
        <div className="d-flex align-items-center flex-grow-1 w-100">
          {/* Search icon - always visible */}
          <Search size={18} className="text-muted me-2" />
          
          {/* Input field - shows search term when open, selected count when closed */}
          <input
            type="text"
            className="border-0 flex-grow-1 w-100"
            style={{
              outline: "none",
              background: "transparent",
              minWidth: 0,
            }}
            placeholder={placeholder}
            value={isOpen ? searchTerm : getSelectedText()}
            onFocus={() => setIsOpen(true)}
            onChange={(e) => setSearchTerm(e.target.value)}
            readOnly={!isOpen}  // Only editable when dropdown is open
          />
        </div>
        
        {/* Chevron icon - indicates dropdown state */}
        <ChevronDown size={18} className="text-muted flex-shrink-0" />
      </div>

      {/* ================================================================== */}
      {/* DROPDOWN PANEL - ONLY VISIBLE WHEN OPEN */}
      {/* ================================================================== */}
      {isOpen && (
        <div
          className="card position-absolute w-100 mt-1"
          style={{
            zIndex: 1000,
            maxHeight: "350px",
            border: "1px solid #dee2e6",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* ============================================================== */}
          {/* SCROLLABLE LIST SECTION */}
          {/* ============================================================== */}
          <div
            className="list-group list-group-flush flex-grow-1"
            style={{ overflowY: "auto", maxHeight: "260px" }}
          >
            {/* Select All checkbox item */}
            <div
              className="list-group-item list-group-item-action d-flex align-items-center"
              style={{ cursor: "pointer" }}
              onClick={handleSelectAll}
            >
              <input
                type="checkbox"
                className="form-check-input me-3"
                checked={allFilteredSelected}
                onChange={handleSelectAll}
              />
              <span className="fw-semibold">Select All</span>
            </div>

            {/* Individual option items */}
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="list-group-item list-group-item-action d-flex align-items-center"
                style={{ cursor: "pointer" }}
                onClick={() => toggleItem(item)}
              >
                <input
                  type="checkbox"
                  className="form-check-input me-3"
                  checked={localSelected.some((i) => i.id === item.id)}
                  onChange={() => toggleItem(item)}
                />
                <span>{item[displayValue]}</span>
              </div>
            ))}

            {/* Empty state message */}
            {filteredItems.length === 0 && (
              <div className="list-group-item text-center text-muted">
                No options found
              </div>
            )}
          </div>

          {/* ============================================================== */}
          {/* STICKY FOOTER WITH ACTION BUTTONS */}
          {/* ============================================================== */}
          <div
            className="card-footer bg-white d-flex justify-content-between align-items-center p-2 border-top"
            style={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "#fff",
              zIndex: 10,
              gap: "4px",
            }}
          >
            {/* Apply button - commits changes and closes dropdown */}
            <button
              onClick={handleApply}
              className="btn btn-outline-primary btn-sm"
              style={{ flex: 1, padding: "2px 2px", fontSize: "10px" }}
            >
              Apply
            </button>
            
            {/* Cancel button - discards changes and closes dropdown */}
            <button
              onClick={handleCancel}
              className="btn btn-outline-primary btn-sm"
              style={{ flex: 1, padding: "2px 2px", fontSize: "10px" }}
            >
              Cancel
            </button>
            
            {/* Clear button - removes all selections */}
            <button
              onClick={handleClear}
              className="btn btn-outline-danger btn-sm"
              style={{ flex: 1, padding: "2px 2px", fontSize: "10px" }}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
